import styled from '@emotion/styled'

import { metrics, zIndex } from 'component-library/__styling/settings'
import { breakpoint, calcRem } from 'component-library/__styling/style-functions'
import { supports } from 'component-library/__helpers/at-supports-helper'

import { styledListClassName } from 'component-library/typography/List/List.styled'
import { Sharing } from 'component-library/buttons/Sharing/Sharing'
import { styledWrapperClassName as relatedContentClassname } from 'component-library/content/RelatedContent/RelatedContent.styled'

export interface Props {
    isHeroImage?: boolean
}

const gridGapSize = calcRem(metrics.thewest.margins.sm)

export const StyledGrid = styled('article')(
    {
        margin: '0 auto',
        width: '100%',

        display: 'table', // Grid FallBack
        tableLayout: 'fixed', // Required for IE so content does not overflow
        marginBottom: metrics.thewest.margins.md,

        // If grid is supported use css grid
    },
    supports('display: grid', {
        display: 'grid', // Override display: table
        gridTemplateColumns: '100%',
        gridGap: gridGapSize,

        '> .row': {
            display: 'table-cell',
        },
    })
)

export const StyledCell = styled('div')(
    {
        display: 'block',
        padding: `0 0 ${gridGapSize} 0`,
    },
    supports('display: grid', {
        padding: 0,
    })
)

export const StyledFlexCell = styled(StyledCell)({
    display: 'flex',

    [`& > .${relatedContentClassname}`]: {
        flexBasis: '50%',
        flexGrow: 1,
    },
})

export const StyledNestedGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'auto',
    gridGap: gridGapSize,

    [breakpoint('xs')]: {
        gridTemplateColumns: '40px auto',
        padding: `${calcRem(metrics.thewest.margins.md)} 0`,
    },
})

export const StyledAside = styled('aside')(
    {
        marginBottom: calcRem(metrics.thewest.margins.md),
    },
    supports('display: grid', {
        marginBottom: 'unset',
    })
)

export const StyledSharing = styled(Sharing)({
    position: 'sticky',
    top: metrics.thewest.margins.md,
})

export const StyledArticleContent = styled('div')({
    /*
            - Adds the horizontal padding to the article content
            - Excludes the figure component so we can center images & video of unknown width
              but also have the image asset full bleed to the edge on desktop.
        */
    width: '100%',

    '& > *:last-child': {
        marginBottom: 0,
    },

    // If the last item in an article is an image then remove the border on the figcaption so it doesn't double up with topics
    '& > figure:last-child figcaption': {
        borderBottom: 'none',
    },

    // Fix for SWM-4447
    '& .twin-image-wrapper': {
        marginBottom: calcRem(metrics.thewest.margins.md),

        [breakpoint('sm')]: {
            marginBottom: 0,
        },
    },

    [`& .${styledListClassName}`]: {
        paddingLeft: calcRem(metrics.thewest.margins.lg),
    },
})

export const StyledStickyCell = styled('div')(
    {
        '--stickyHeight': 0,
        display: 'block',
        padding: `0 0 ${gridGapSize} 0`,
        minHeight: `var(--stickyHeight)`,

        '&.is-sticky': {
            left: '100%',
            transform: 'translateX(366px)',
            position: 'sticky',
            top: 168,
            width: 321,
            zIndex: zIndex.common.stickyVideo,
            pointerEvents: 'auto',

            // If there is a figcaption we don't want it to be sticky.
            '& .figcaption': {
                visibility: 'hidden',
            },
        },
    },
    supports('display: grid', {
        padding: 0,
    })
)
StyledStickyCell.displayName = 'StickyContainer'

export const StyledStickyTop = styled('div')({
    height: 2, // set to 2px to reduce weird rendering bugs and flickering of the video
})
StyledStickyTop.displayName = 'StickyWatcher'
